import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { GlobalService } from '../services/global.service';
import { inject, PLATFORM_ID } from '@angular/core';
import { UserSessionService } from '../services/user-session.service';
import { REQUEST } from '../../../express.token';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { environment } from '../../../environments/environment';
import { V2_ENDPOINT_PATTERNS } from '../config/v2-endpoints.config';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

/**
 * Add or remove header values
 */

export const AuthInterceptor: HttpInterceptorFn = (request, next) => {

    // Angular doc is saying interceptor functions are in injection context,
    // but using inject was throwing error
    // https://angular.dev/guide/http/interceptors#dependency-injection-in-interceptors

    const userSessionService = inject(UserSessionService);
    const expressReq = inject(REQUEST, { optional: true });
    const platformId = inject(PLATFORM_ID);

    if (request.method === 'POST' || request.method === 'PUT' || request.method === 'PATCH') {
        // If header already has a content type, use it. Else set application/json.
        if (!request.headers.has('Content-Type')) {
            request = request.clone({
                headers: request.headers.set('Content-Type', 'application/json'),
            });
        }

        // For form data, we should not pass any Content-Type.
        // Track this under https://github.com/angular/angular/issues/13241
        if (request.headers.get('Content-Type') === 'multipart/form-data') {
            request = request.clone({ headers: request.headers.delete('Content-Type') });
        }
    }

    if (userSessionService.getAccessToken()) {
        // To add the authorization header
        request = request.clone({
            setHeaders: {
                Authorization: `Bearer ${userSessionService.getAccessToken()}`,
            },
        });
    }
    
    let clientDomain = environment.client_domain || '';

    if (isPlatformServer(platformId) && environment.production) {
        clientDomain = expressReq?.headers?.host!;
        
        const cfConnectingIp = expressReq?.headers?.['cf-connecting-ip'];
        try {
            if (cfConnectingIp) {
                request = request.clone({
                    headers: request.headers
                        .set('X-Forwarded-For', `${cfConnectingIp}`)
                });
            }
        } catch (error) {
            console.warn('ip_addresses_error', { error });
        }
    }

    if (isPlatformBrowser(platformId) && environment.production) {
        clientDomain = window.location.origin;
    }

    let clientHostname = clientDomain;
    try {
        clientHostname = new URL(
            clientDomain.startsWith('http') ? clientDomain : `https://${clientDomain}`
        ).hostname;
    } catch {
        console.warn(`Invalid clientDomain: ${clientDomain}`);
    }


    request = request.clone({
        headers: request.headers
            .set('X-Client-Domain', `${clientDomain}`)
            .set('X-Requested-With', clientHostname)
            .set('Timezone', Intl.DateTimeFormat().resolvedOptions().timeZone)
    });

    // Rewrite api/v1/ → api/v2/ for endpoints that have been migrated to v2 (all domains)
    if (V2_ENDPOINT_PATTERNS.some((pattern) => pattern.test(request.url))) {
        request = request.clone({ url: request.url.replace('/api/v1/', '/api/v2/') });
    }
    
    return next(request);
};